import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  BellRing,
  Building2,
  CheckCircle2,
  Circle,
  Clock,
  FileText,
  Loader2,
  Mail,
  MapPin,
  MessageSquare,
  Navigation,
  Phone,
  RotateCcw,
  Save,
  ShieldCheck,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/layouts/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { usePartners, type Partner } from "@/context/PartnerContext";
import { useLeads, type ServiceType } from "@/context/LeadContext";
import { getCsrfToken } from "@/integrations/php/client";
import { SERVICE_OPTIONS } from "@/lib/serviceCatalog";

const SERVICES: Array<{ value: ServiceType; label: string }> = SERVICE_OPTIONS.map(({ value, label }) => ({ value, label }));

const TIMEFRAMES = [
  { value: "άμεσα", label: "Άμεσα" },
  { value: "εντός3μηνών", label: "Εντός 3 μηνών" },
  { value: "κάνωΈρευνα", label: "Κάνω έρευνα αγοράς" },
];

const SOURCE_LABELS = [
  { value: "phone", label: "Τηλέφωνο" },
  { value: "website", label: "Website" },
  { value: "email", label: "Email" },
  { value: "other", label: "Άλλο" },
];

const DRAFT_KEY = "new_manual_lead_draft_v1";
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const INITIAL_FORM = {
  name: "",
  phone: "",
  email: "",
  address: "",
  postal_code: "",
  city: "",
  service: "",
  timeframe: "κάνωΈρευνα",
  comments: "",
  partner_id: "",
  source_label: "phone",
};

type FormState = typeof INITIAL_FORM;
type FormField = keyof FormState;
type SuggestedPartner = Partner & { distance?: number; withinRadius?: boolean };

function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const radius = 6371;
  const latitudeDelta = (lat2 - lat1) * (Math.PI / 180);
  const longitudeDelta = (lon2 - lon1) * (Math.PI / 180);
  const value =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(longitudeDelta / 2) ** 2;
  return radius * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}

const restoreDraft = (): { form: FormState; notify: boolean; restored: boolean } => {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return { form: INITIAL_FORM, notify: true, restored: false };
    const parsed = JSON.parse(raw) as { form?: Partial<FormState>; notify?: boolean; savedAt?: number };
    if (!parsed.savedAt || Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      sessionStorage.removeItem(DRAFT_KEY);
      return { form: INITIAL_FORM, notify: true, restored: false };
    }
    return {
      form: { ...INITIAL_FORM, ...(parsed.form ?? {}) },
      notify: parsed.notify ?? true,
      restored: true,
    };
  } catch {
    return { form: INITIAL_FORM, notify: true, restored: false };
  }
};

const FieldError = ({ id, message }: { id: string; message?: string }) =>
  message ? <p id={id} className="mt-1.5 text-xs font-medium text-rose-600">{message}</p> : null;

const NewManualLead = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { partners, loading: partnersLoading } = usePartners();
  const { refreshLeads } = useLeads();
  const initialDraft = useRef(restoreDraft()).current;

  const [form, setForm] = useState<FormState>(initialDraft.form);
  const [notify, setNotify] = useState(initialDraft.notify);
  const [submitting, setSubmitting] = useState(false);
  const [suggestedPartners, setSuggestedPartners] = useState<SuggestedPartner[]>([]);
  const [geocoding, setGeocoding] = useState(false);
  const [inputCoords, setInputCoords] = useState<{ lat: number; lng: number; locationKey: string } | null>(null);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [draftRestored, setDraftRestored] = useState(initialDraft.restored);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const sortedPartners = useMemo(
    () => [...partners].filter((partner) => partner.active).sort((a, b) => a.name.localeCompare(b.name, "el")),
    [partners],
  );

  const update = (field: FormField, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setServerErrors((current) => {
      if (!(field in current)) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const fieldErrors = useMemo<Record<string, string>>(() => {
    const errors: Record<string, string> = {};
    const postalCode = form.postal_code.replace(/\s+/g, "");
    if (form.name.trim().length < 2 || form.name.trim().length > 180) {
      errors.name = "Συμπληρώστε ονοματεπώνυμο 2–180 χαρακτήρων.";
    }
    if (!/^[0-9+().\s-]{7,25}$/.test(form.phone.trim())) {
      errors.phone = "Συμπληρώστε έγκυρο τηλέφωνο.";
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errors.email = "Συμπληρώστε έγκυρο email.";
    }
    if (!form.service) errors.service = "Επιλέξτε υπηρεσία.";
    if (postalCode && !/^[0-9]{5}$/.test(postalCode)) errors.postal_code = "Ο Τ.Κ. πρέπει να έχει 5 ψηφία.";
    if (form.address.trim().length > 255) errors.address = "Έως 255 χαρακτήρες.";
    if (form.city.trim().length > 120) errors.city = "Έως 120 χαρακτήρες.";
    if (form.comments.length > 4000) errors.comments = "Έως 4.000 χαρακτήρες.";
    return errors;
  }, [form]);

  const visibleErrors = attemptedSubmit ? { ...fieldErrors, ...serverErrors } : serverErrors;
  const canSubmit = Object.keys(fieldErrors).length === 0;
  const compatiblePartners = useMemo(
    () => sortedPartners.filter((partner) =>
      !form.service || form.service === "άλλο" || partner.services.includes(form.service as ServiceType)
    ),
    [form.service, sortedPartners],
  );
  const selectedPartner = useMemo(
    () => sortedPartners.find((partner) => partner.id === form.partner_id),
    [form.partner_id, sortedPartners],
  );
  const selectedSuggestion = useMemo(
    () => suggestedPartners.find((partner) => partner.id === form.partner_id),
    [form.partner_id, suggestedPartners],
  );
  const hasLocation = Boolean(form.address.trim() || form.city.trim() || form.postal_code.trim());
  const serviceLabel = SERVICES.find((service) => service.value === form.service)?.label;
  const sourceLabel = SOURCE_LABELS.find((source) => source.value === form.source_label)?.label;
  const requiredChecks = [
    { label: "Στοιχεία επαφής", complete: form.name.trim().length >= 2 && /^[0-9+().\s-]{7,25}$/.test(form.phone.trim()) },
    { label: "Υπηρεσία ενδιαφέροντος", complete: Boolean(form.service) },
    { label: "Κανόνας ανάθεσης", complete: Boolean(form.service) },
  ];
  const completion = Math.round((requiredChecks.filter((check) => check.complete).length / requiredChecks.length) * 100);

  const isDirty = useMemo(() => {
    if (submitting) return false;
    return Object.keys(INITIAL_FORM).some((key) => form[key as FormField] !== INITIAL_FORM[key as FormField]) || notify !== true;
  }, [form, notify, submitting]);

  useEffect(() => {
    if (!isDirty) {
      sessionStorage.removeItem(DRAFT_KEY);
      return;
    }
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ form, notify, savedAt: Date.now() }));
    } catch {
      // Draft autosave is best-effort when browser storage is unavailable.
    }
  }, [form, isDirty, notify]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  useEffect(() => {
    if (partnersLoading || !form.partner_id) return;
    if (!compatiblePartners.some((partner) => partner.id === form.partner_id)) {
      setForm((current) => ({ ...current, partner_id: "" }));
    }
  }, [compatiblePartners, form.partner_id, partnersLoading]);

  const goBackSafely = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/admin/leads");
  }, [navigate]);

  const handleBackClick = useCallback(() => {
    if (isDirty) setShowLeaveDialog(true);
    else goBackSafely();
  }, [goBackSafely, isDirty]);

  const discardDraft = useCallback(() => {
    sessionStorage.removeItem(DRAFT_KEY);
    setForm(INITIAL_FORM);
    setNotify(true);
    setDraftRestored(false);
    setAttemptedSubmit(false);
    setServerErrors({});
  }, []);

  useEffect(() => {
    if (!form.service) {
      setSuggestedPartners([]);
      return;
    }

    const fetchSuggestions = async () => {
      setGeocoding(true);
      try {
        const locationKey = `${form.city.trim().toLocaleLowerCase("el-GR")}|${form.postal_code.replace(/\s+/g, "")}`;
        const cachedCoords = inputCoords?.locationKey === locationKey ? inputCoords : null;
        let inputLat = cachedCoords?.lat;
        let inputLng = cachedCoords?.lng;

        if ((form.city.trim() || form.postal_code.trim()) && !cachedCoords) {
          try {
            const response = await fetch(
              `/api/geocode-location.php?city=${encodeURIComponent(form.city)}&postal_code=${encodeURIComponent(form.postal_code)}`,
              { credentials: "include", headers: { "X-CSRF-Token": await getCsrfToken() } },
            );
            if (response.ok) {
              const data = await response.json();
              if (Number.isFinite(Number(data.latitude)) && Number.isFinite(Number(data.longitude))) {
                inputLat = Number(data.latitude);
                inputLng = Number(data.longitude);
                setInputCoords({ lat: inputLat, lng: inputLng, locationKey });
              }
            }
          } catch (error) {
            console.error("Manual lead geocoding failed", error);
          }
        }

        let matched: SuggestedPartner[] = compatiblePartners.map((partner) => ({ ...partner }));
        if (typeof inputLat === "number" && typeof inputLng === "number") {
          matched = matched
            .map((partner) => {
              const hasCoordinates = typeof partner.latitude === "number" && typeof partner.longitude === "number";
              const distance = hasCoordinates
                ? calculateDistance(inputLat, inputLng, partner.latitude as number, partner.longitude as number)
                : 999;
              const radius = Number(partner.radius ?? 0);
              return { ...partner, distance, withinRadius: distance !== 999 && radius > 0 && distance <= radius };
            })
            .sort((a, b) => {
              if (a.withinRadius !== b.withinRadius) return a.withinRadius ? -1 : 1;
              if ((a.distance ?? 999) !== (b.distance ?? 999)) return (a.distance ?? 999) - (b.distance ?? 999);
              return (a.leadCount ?? 0) - (b.leadCount ?? 0);
            });
        } else {
          matched.sort((a, b) => (a.leadCount ?? 0) - (b.leadCount ?? 0) || a.name.localeCompare(b.name, "el"));
        }
        setSuggestedPartners(matched.slice(0, 5));
      } finally {
        setGeocoding(false);
      }
    };

    const debounce = window.setTimeout(() => void fetchSuggestions(), 500);
    return () => window.clearTimeout(debounce);
  }, [compatiblePartners, form.city, form.postal_code, form.service, inputCoords]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setAttemptedSubmit(true);
    setServerErrors({});
    if (!canSubmit) {
      const firstInvalidField = Object.keys(fieldErrors)[0];
      window.requestAnimationFrame(() => document.getElementById(firstInvalidField)?.focus());
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch("/api/create-manual-lead.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": await getCsrfToken() },
        credentials: "include",
        body: JSON.stringify({ ...form, partner_id: form.partner_id || undefined, notify }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (data.errors && typeof data.errors === "object") setServerErrors(data.errors);
        throw new Error(typeof data.error === "string" ? data.error : "Αποτυχία αποθήκευσης");
      }

      toast({
        title: "Η επαφή δημιουργήθηκε",
        description: data.partner_id
          ? notify && Number(data.notifications_queued) > 0
            ? `Ανατέθηκε και μπήκαν ${data.notifications_queued} ειδοποιήσεις στην ουρά.`
            : "Ανατέθηκε χωρίς δημιουργία ειδοποιήσεων."
          : "Έμεινε χωρίς συνεργάτη και εμφανίζεται στο Operations Inbox.",
      });
      sessionStorage.removeItem(DRAFT_KEY);
      await refreshLeads();
      navigate(`/admin/lead/${data.lead_id}`);
    } catch (error: unknown) {
      toast({
        title: "Η καταχώρηση δεν ολοκληρώθηκε",
        description: error instanceof Error ? error.message : "Δοκιμάστε ξανά.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-5">
      <PageHeader
        eyebrow="Επαφές"
        title="Νέα χειροκίνητη επαφή"
        description="Καταχώρηση από τηλέφωνο, επίσκεψη ή σύσταση, με ασφαλή ανάθεση και ελεγχόμενες ειδοποιήσεις."
        icon={<UserPlus />}
        iconGradient="blue"
        actions={
          <Button type="button" variant="outline" onClick={handleBackClick} className="h-11 w-full gap-2 sm:w-auto">
            <ArrowLeft className="h-4 w-4" /> Επιστροφή
          </Button>
        }
      />

      {draftRestored && isDirty && (
        <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <FileText className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div>
              <p className="text-sm font-semibold text-amber-950">Επαναφέρθηκε το τελευταίο draft</p>
              <p className="mt-0.5 text-xs leading-5 text-amber-800">Τα στοιχεία παραμένουν μόνο σε αυτόν τον browser για έως 24 ώρες.</p>
            </div>
          </div>
          <Button type="button" variant="outline" onClick={discardDraft} className="h-11 shrink-0 gap-2 border-amber-300 bg-white text-amber-900 hover:bg-amber-100">
            <X className="h-4 w-4" /> Καθαρισμός draft
          </Button>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-5">
            <Card className="overflow-hidden rounded-2xl border-slate-200 shadow-sm">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Πρόοδος καταχώρησης</p>
                    <p className="mt-0.5 text-xs text-slate-500">Τρία βασικά βήματα πριν από τη δημιουργία.</p>
                  </div>
                  <span className="text-sm font-bold tabular-nums text-sky-700">{completion}%</span>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100" aria-label={`Πρόοδος ${completion}%`}>
                  <div className="h-full rounded-full bg-sky-600 transition-all" style={{ width: `${completion}%` }} />
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sm font-bold text-sky-700">01</span>
                  <div>
                    <CardTitle className="text-base text-slate-950">Στοιχεία επαφής</CardTitle>
                    <CardDescription className="mt-1">Το όνομα και το τηλέφωνο είναι υποχρεωτικά.</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label htmlFor="name">Ονοματεπώνυμο *</Label>
                  <Input id="name" autoComplete="name" value={form.name} onChange={(event) => update("name", event.target.value)} aria-invalid={Boolean(visibleErrors.name)} aria-describedby={visibleErrors.name ? "name-error" : undefined} placeholder="π.χ. Γιώργος Παπαδόπουλος" className="mt-1.5 h-11" />
                  <FieldError id="name-error" message={visibleErrors.name} />
                </div>
                <div>
                  <Label htmlFor="phone" className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Τηλέφωνο *</Label>
                  <Input id="phone" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(event) => update("phone", event.target.value)} aria-invalid={Boolean(visibleErrors.phone)} aria-describedby={visibleErrors.phone ? "phone-error" : undefined} placeholder="6912345678" className="mt-1.5 h-11" />
                  <FieldError id="phone-error" message={visibleErrors.phone} />
                </div>
                <div>
                  <Label htmlFor="email" className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</Label>
                  <Input id="email" type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(event) => update("email", event.target.value)} aria-invalid={Boolean(visibleErrors.email)} aria-describedby={visibleErrors.email ? "email-error" : undefined} placeholder="name@example.com" className="mt-1.5 h-11" />
                  <FieldError id="email-error" message={visibleErrors.email} />
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-sm font-bold text-violet-700">02</span>
                  <div>
                    <CardTitle className="text-base text-slate-950">Ενδιαφέρον</CardTitle>
                    <CardDescription className="mt-1">Η υπηρεσία καθορίζει τους επιλέξιμους συνεργάτες.</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="service">Υπηρεσία *</Label>
                  <Select value={form.service} onValueChange={(value) => update("service", value)}>
                    <SelectTrigger id="service" aria-invalid={Boolean(visibleErrors.service)} aria-describedby={visibleErrors.service ? "service-error" : undefined} className="mt-1.5 h-11"><SelectValue placeholder="Επιλέξτε υπηρεσία" /></SelectTrigger>
                    <SelectContent>{SERVICES.map((service) => <SelectItem key={service.value} value={service.value}>{service.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <FieldError id="service-error" message={visibleErrors.service} />
                </div>
                <div>
                  <Label htmlFor="timeframe" className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Χρονοδιάγραμμα</Label>
                  <Select value={form.timeframe} onValueChange={(value) => update("timeframe", value)}>
                    <SelectTrigger id="timeframe" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIMEFRAMES.map((timeframe) => <SelectItem key={timeframe.value} value={timeframe.value}>{timeframe.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-sm font-bold text-emerald-700">03</span>
                  <div>
                    <CardTitle className="text-base text-slate-950">Τοποθεσία & κάλυψη</CardTitle>
                    <CardDescription className="mt-1">Η αυτόματη ανάθεση γίνεται μόνο όταν υπάρχει συνεργάτης εντός ακτίνας.</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_120px]">
                  <div>
                    <Label htmlFor="address">Διεύθυνση</Label>
                    <Input id="address" autoComplete="street-address" value={form.address} onChange={(event) => update("address", event.target.value)} aria-invalid={Boolean(visibleErrors.address)} aria-describedby={visibleErrors.address ? "address-error" : undefined} placeholder="Οδός και αριθμός" className="mt-1.5 h-11" />
                    <FieldError id="address-error" message={visibleErrors.address} />
                  </div>
                  <div>
                    <Label htmlFor="city">Πόλη</Label>
                    <Input id="city" autoComplete="address-level2" value={form.city} onChange={(event) => update("city", event.target.value)} aria-invalid={Boolean(visibleErrors.city)} aria-describedby={visibleErrors.city ? "city-error" : undefined} placeholder="π.χ. Αθήνα" className="mt-1.5 h-11" />
                    <FieldError id="city-error" message={visibleErrors.city} />
                  </div>
                  <div>
                    <Label htmlFor="postal_code">Τ.Κ.</Label>
                    <Input id="postal_code" inputMode="numeric" autoComplete="postal-code" maxLength={5} value={form.postal_code} onChange={(event) => update("postal_code", event.target.value)} aria-invalid={Boolean(visibleErrors.postal_code)} aria-describedby={visibleErrors.postal_code ? "postal-code-error" : undefined} placeholder="11526" className="mt-1.5 h-11" />
                    <FieldError id="postal-code-error" message={visibleErrors.postal_code} />
                  </div>
                </div>

                {form.service && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3.5 sm:p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Navigation className="h-4 w-4 text-sky-700" /> Κατάλληλοι συνεργάτες</p>
                        <p className="mt-1 text-xs leading-5 text-slate-500">Προτάσεις βάσει υπηρεσίας, απόστασης και τρέχοντος φόρτου.</p>
                      </div>
                      {geocoding && <Loader2 className="h-4 w-4 animate-spin text-sky-700" aria-label="Υπολογισμός αποστάσεων" />}
                    </div>
                    {partnersLoading ? (
                      <div className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Φόρτωση δικτύου…</div>
                    ) : suggestedPartners.length > 0 ? (
                      <div className="mt-4 grid gap-2 sm:grid-cols-2">
                        {suggestedPartners.map((partner) => {
                          const selected = form.partner_id === partner.id;
                          const hasDistance = typeof partner.distance === "number" && partner.distance !== 999;
                          return (
                            <button key={partner.id} type="button" aria-pressed={selected} onClick={() => update("partner_id", selected ? "" : partner.id)} className={`min-h-24 rounded-xl border p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${selected ? "border-sky-500 bg-white ring-1 ring-sky-200" : "border-slate-200 bg-white hover:border-sky-300"}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-slate-900">{partner.name}</p>
                                  <p className="mt-1 flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3 w-3" /> {partner.location || "Χωρίς καταγεγραμμένη έδρα"}</p>
                                </div>
                                {selected ? <CheckCircle2 className="h-5 w-5 shrink-0 text-sky-700" /> : <Circle className="h-5 w-5 shrink-0 text-slate-300" />}
                              </div>
                              <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-medium">
                                {hasDistance && <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">{partner.distance?.toFixed(1)} km</span>}
                                {hasDistance && <span className={`rounded-full px-2 py-1 ${partner.withinRadius ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{partner.withinRadius ? "Εντός ακτίνας" : "Εκτός ακτίνας"}</span>}
                                <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">{partner.leadCount ?? 0} leads</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-4 text-sm text-slate-500">Δεν υπάρχει ενεργός συνεργάτης για αυτή την υπηρεσία.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-sm font-bold text-amber-700">04</span>
                  <div>
                    <CardTitle className="text-base text-slate-950">Πηγή & σημειώσεις</CardTitle>
                    <CardDescription className="mt-1">Πρακτικό context για τον συνεργάτη και την ομάδα.</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-[220px_minmax(0,1fr)]">
                <div>
                  <Label htmlFor="source_label">Πηγή</Label>
                  <Select value={form.source_label} onValueChange={(value) => update("source_label", value)}>
                    <SelectTrigger id="source_label" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{SOURCE_LABELS.map((source) => <SelectItem key={source.value} value={source.value}>{source.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="comments" className="flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Σχόλια / σημειώσεις</Label>
                  <Textarea id="comments" value={form.comments} onChange={(event) => update("comments", event.target.value)} aria-invalid={Boolean(visibleErrors.comments)} aria-describedby={visibleErrors.comments ? "comments-error" : "comments-help"} placeholder="Ανάγκες, προτιμώμενη ώρα επικοινωνίας ή άλλη χρήσιμη πληροφορία…" rows={4} className="mt-1.5 min-h-28 resize-y" />
                  <div className="mt-1.5 flex justify-between gap-3 text-xs text-slate-400"><span id="comments-help">Ορατές στον συνεργάτη.</span><span className="tabular-nums">{form.comments.length}/4000</span></div>
                  <FieldError id="comments-error" message={visibleErrors.comments} />
                </div>
              </CardContent>
            </Card>
          </div>

          <aside className="lg:sticky lg:top-24">
            <Card className="overflow-hidden rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100 bg-slate-50/70 pb-4">
                <CardTitle className="text-base text-slate-950">Έλεγχος & δημιουργία</CardTitle>
                <CardDescription>Επιβεβαιώστε ανάθεση και ειδοποιήσεις.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5 p-4 sm:p-5">
                <div className="space-y-2.5">
                  {requiredChecks.map((check) => (
                    <div key={check.label} className="flex items-center gap-2.5 text-sm">
                      {check.complete ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> : <Circle className="h-4 w-4 shrink-0 text-slate-300" />}
                      <span className={check.complete ? "text-slate-700" : "text-slate-500"}>{check.label}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-slate-100 pt-5">
                  <Label htmlFor="partner_id">Ανάθεση συνεργάτη</Label>
                  <Select value={form.partner_id || "__auto__"} onValueChange={(value) => update("partner_id", value === "__auto__" ? "" : value)} disabled={!form.service || partnersLoading}>
                    <SelectTrigger id="partner_id" aria-invalid={Boolean(visibleErrors.partner_id)} aria-describedby={visibleErrors.partner_id ? "partner-error" : undefined} className="mt-1.5 h-11"><SelectValue placeholder="Αυτόματη ανάθεση" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__auto__">Αυτόματη ανάθεση</SelectItem>
                      {compatiblePartners.map((partner) => <SelectItem key={partner.id} value={partner.id}>{partner.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FieldError id="partner-error" message={visibleErrors.partner_id} />
                  <div className="mt-2 rounded-xl bg-slate-50 px-3 py-2.5 text-xs leading-5 text-slate-600">
                    {selectedPartner ? (
                      <span className="flex items-start gap-2"><Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-700" /> Χειροκίνητα στον/στην <strong>{selectedPartner.name}</strong>.</span>
                    ) : hasLocation ? (
                      <span className="flex items-start gap-2"><Navigation className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-700" /> Θα αναζητηθεί αυτόματα συνεργάτης εντός ακτίνας. Αν δεν βρεθεί, η επαφή θα μείνει στο Operations Inbox.</span>
                    ) : (
                      <span className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" /> Χωρίς τοποθεσία δεν γίνεται αυτόματη ανάθεση. Η επαφή θα μείνει στο Operations Inbox.</span>
                    )}
                  </div>
                  {selectedSuggestion?.withinRadius === false && (
                    <p className="mt-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-5 text-amber-900"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Χειροκίνητη επιλογή εκτός δηλωμένης ακτίνας. Επιβεβαιώστε πριν τη δημιουργία.</p>
                  )}
                </div>

                <div className="border-t border-slate-100 pt-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-start gap-2.5">
                      <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
                      <div>
                        <Label htmlFor="notify" className="cursor-pointer text-sm font-semibold">Δημιουργία ειδοποιήσεων</Label>
                        <p className="mt-1 text-xs leading-5 text-slate-500">Email και επιτρεπόμενο SMS μπαίνουν στην ασφαλή ουρά μετά την ανάθεση.</p>
                      </div>
                    </div>
                    <Switch id="notify" checked={notify} onCheckedChange={setNotify} aria-label="Δημιουργία ειδοποιήσεων" />
                  </div>
                  {!notify && <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">Η επαφή θα αποθηκευτεί χωρίς κανένα notification job.</p>}
                </div>

                <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-3 text-xs leading-5 text-emerald-900">
                  <p className="flex items-center gap-2 font-semibold"><ShieldCheck className="h-4 w-4" /> Ατομική καταχώρηση</p>
                  <p className="mt-1">Lead, ανάθεση, alert και επιλεγμένες ειδοποιήσεις αποθηκεύονται μαζί ή δεν αποθηκεύεται τίποτα.</p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
                  <p className="font-semibold text-slate-900">Σύνοψη</p>
                  <dl className="mt-2 space-y-1.5">
                    <div className="flex justify-between gap-3"><dt>Υπηρεσία</dt><dd className="text-right font-medium text-slate-800">{serviceLabel || "—"}</dd></div>
                    <div className="flex justify-between gap-3"><dt>Πηγή</dt><dd className="text-right font-medium text-slate-800">{sourceLabel || "—"}</dd></div>
                    <div className="flex justify-between gap-3"><dt>Ανάθεση</dt><dd className="max-w-44 truncate text-right font-medium text-slate-800">{selectedPartner?.name || "Αυτόματη"}</dd></div>
                  </dl>
                </div>

                <Button type="submit" disabled={submitting} className="h-12 w-full gap-2 text-sm font-semibold">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {submitting ? "Αποθήκευση…" : "Δημιουργία επαφής"}
                </Button>
                {!canSubmit && attemptedSubmit && <p className="text-center text-xs font-medium text-rose-600">Διορθώστε τα πεδία που επισημαίνονται.</p>}
                <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-400"><RotateCcw className="h-3 w-3" /> Το draft αποθηκεύεται αυτόματα για 24 ώρες.</p>
              </CardContent>
            </Card>
          </aside>
        </div>
      </form>

      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Να φύγετε από την καταχώρηση;</AlertDialogTitle>
            <AlertDialogDescription>Οι αλλαγές δεν έχουν δημιουργήσει ακόμη επαφή. Το draft θα παραμείνει σε αυτόν τον browser για έως 24 ώρες, ώστε να συνεχίσετε αργότερα.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Συνέχεια επεξεργασίας</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowLeaveDialog(false); goBackSafely(); }}>Έξοδος — κράτησε το draft</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NewManualLead;
